import React from 'react'
import { Tooltip } from '@mui/material'
import type { UnselectableValueItem } from '../../types/codeIndex'
import { UNSELECTABLE_VALUES_RECHECK_INSTRUCTION } from './UnselectableValuesBanner'

export const UNSELECTABLE_VALUE_MESSAGE_TITLE = 'Never selectable. These rules make this value impossible:'

type UnselectableValueBadgeProps = {
  item: UnselectableValueItem
  // The flagged value or a rule it names changed since the last save; only a save can re-evaluate it.
  needsRecheck: boolean
  testId?: string
}

/**
 * Compact marker shown under a flagged option code. The row stays one line tall: the rules that
 * make the value impossible are listed in a tooltip, so a value with many rules does not push the
 * table apart.
 */
export default function UnselectableValueBadge({ item, needsRecheck, testId }: UnselectableValueBadgeProps) {
  const rules = item.rules ?? []
  const detail = (
    <div className="max-h-[60vh] max-w-[440px] overflow-auto whitespace-normal text-[12px] leading-4 text-[#B3261E]">
      <div className="font-semibold">{UNSELECTABLE_VALUE_MESSAGE_TITLE}</div>
      {rules.map((line, index) => (
        <div key={`${line.ruleId ?? line.formatValueId ?? index}:${index}`} className="mt-0.5">· {line.text}</div>
      ))}
      {needsRecheck && <div className="mt-1 italic">{UNSELECTABLE_VALUES_RECHECK_INSTRUCTION}</div>}
    </div>
  )

  return (
    <div data-testid={testId} className="mt-1">
      <Tooltip
        title={detail}
        placement="right"
        arrow
        enterDelay={150}
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: '#FFF5F5',
              border: '1px solid #F3C2BE',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
              maxWidth: 'none',
              padding: '8px 10px',
            },
          },
          arrow: { sx: { color: '#FFF5F5', '&::before': { border: '1px solid #F3C2BE' } } },
        }}
      >
        <span
          className="inline-flex cursor-help items-center gap-1 whitespace-nowrap rounded-full border border-[#B3261E] bg-[#FFF5F5] px-1.5 py-px text-[10px] font-semibold text-[#B3261E]"
          aria-label={needsRecheck ? `Never selectable. ${UNSELECTABLE_VALUES_RECHECK_INSTRUCTION}` : 'Never selectable'}
        >
          <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#B3261E]" />
          Never selectable{needsRecheck ? ' · save to recheck' : ''}
        </span>
      </Tooltip>
    </div>
  )
}
